// src/lib/axios.ts
import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";

const customAxios = axios.create({
  baseURL: process.env.REACT_APP_TRADEASY_API,
});

// ---- Request interceptor: añade x-user-hash de forma segura (compatible v0.27/v1)
customAxios.interceptors.request.use(
  (config: any) => {
    try {
      const userHash = localStorage.getItem("user_id");

      if (userHash) {
        if (!config.headers) config.headers = {};
        const h: any = config.headers;
        if (h && typeof h.set === "function") {
          // Axios v1: AxiosHeaders
          h.set("x-user-hash", userHash);
        } else {
          // Axios v0.27: objeto plano
          h["x-user-hash"] = userHash;
        }
      }
    } catch {
      // entornos sin localStorage (SSR/tests)
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// ---- Response interceptor: trata 401 por sesión inválida/revocada
customAxios.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError<any>) => {
    const status = error.response?.status ?? 0;
    const code = (error.response?.data as any)?.code;

    if (
      status === 401 &&
      (code === "SESSION_INVALID" || code === "SESSION_REVOKED")
    ) {
      try {
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_email");
        localStorage.removeItem("type_user");
        localStorage.removeItem("user_valid_count");
      } catch {
        console.debug("No localStorage available");
      }
      if (typeof window !== "undefined") window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// ---- axios-retry (sin onRetry para cuadrar con typings)
axiosRetry(customAxios, {
  retries: 3,
  retryDelay: (retryCount: number) =>
    Math.min(1000 * 2 ** (retryCount - 1), 5000),
  retryCondition: (error: AxiosError) => {
    if (axiosRetry.isNetworkOrIdempotentRequestError(error)) return true;
    const status = error.response?.status ?? 0;
    return status >= 500;
  },
});

export const makeCancelable = () => {
  if (typeof AbortController !== "undefined") {
    const controller = new AbortController();
    return {
      // para axios v1
      signal: controller.signal as AbortSignal,
      cancel: () => controller.abort(),
    };
  }
  // fallback para axios v0.27
  const source = axios.CancelToken.source();
  return {
    cancelToken: source.token,
    cancel: (reason?: any) => source.cancel(reason),
  };
};

export const isCanceled = (err: any) =>
  // axios v0.27
  (axios.isCancel?.(err) ?? false) ||
  // axios v1 / AbortController
  err?.code === "ERR_CANCELED" ||
  err?.name === "CanceledError" ||
  err?.name === "AbortError";
export default customAxios;
